import type { TimeSeries } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';

import { PLOT_GRID_INDEX } from '../config/chartLayout';
import { SLIDER_PREVIEW_SERIES_ID } from '../config/componentIds';
import type { LineDatum } from '../plot/level-of-detail/lineData';
import { getOverviewLineSeriesData } from '../plot/level-of-detail/lineSeriesDataCache';
import type { OptionSection } from '../types/OptionSection.types';
import type { TimeDomain } from '../types/TimeDomain.types';

type BuildSliderPreviewOptionParams = {
  /** The series whose shape the slider should preview; undefined leaves the preview empty. */
  previewSeries: TimeSeries | undefined;
  /** The slider's whole range; the preview is stretched over exactly this, see anchorToDomain. */
  domain: TimeDomain;
  /** Same drawing budget as the visible lines; the preview is drawn (invisibly) on every navigation step too. */
  bucketCount: number;
};

type AnchoredData = { domain: TimeDomain; data: LineDatum[] };

/** Keeps the anchored array's reference while its source and domain stay, like the line caches do. */
const anchoredDataCache = new WeakMap<LineDatum[], AnchoredData>();

function anchorToDomain(source: LineDatum[], domain: TimeDomain): LineDatum[] {
  const cached = anchoredDataCache.get(source);

  if (cached && cached?.domain[0] === domain[0] && cached.domain[1] === domain[1]) {
    return cached.data;
  }

  const data: LineDatum[] = [[domain[0], null], ...source, [domain[1], null]];
  anchoredDataCache.set(source, { domain, data });

  return data;
}

/**
 * ECharts binds its slider shadow to a series once, when the chart is first created, and never
 * re-picks it — reordering series later has no effect, on a merge or a replaceMerge alike. So the
 * preview gets a series of its own that is always present at the same position: swapping *its
 * data* is what changes the shadow, and that works on every update.
 *
 * The series is invisible in the plot and carries the whole domain on purpose: the slider represents
 * the whole domain, so its preview must not shrink as the viewport zooms in. It gets the thinned
 * whole-series overview, not the raw points: although transparent, it is still a line series that
 * ECharts re-processes on every navigation step, so raw data here would cost as much as a visible line.
 */
export function buildSliderPreviewOption({ previewSeries, domain, bucketCount }: BuildSliderPreviewOptionParams) {
  return {
    yAxis: [
      {
        type: 'value',
        gridIndex: PLOT_GRID_INDEX,
        show: false,
        axisPointer: { show: false },
      },
    ],
    series: [
      {
        id: SLIDER_PREVIEW_SERIES_ID,
        type: 'line',
        silent: true,
        showSymbol: false,
        // Drawn in the plot but fully transparent: only its data matters, for the shadow.
        lineStyle: { opacity: 0 },
        itemStyle: { opacity: 0 },
        emphasis: { disabled: true },
        data: previewSeries ? anchorToDomain(getOverviewLineSeriesData(previewSeries, bucketCount).data, domain) : [],
      },
    ],
  } satisfies OptionSection;
}
